import { Component, OnInit } from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterModule } from '@angular/router';

import { MatDrawerMode } from '@angular/material/sidenav';

import { HeaderComponent } from '../header/header.component';
import {
  SidenavComponent,
  SidenavRoleType,
  SidenavType,
} from '../sidenav/sidenav.component';
import { AuthService } from '@services/auth.service';
import { SystemAdminService } from '@services/system-admin.service';
import { NotificationService } from '@services/api/notification.service';
import { NotificationViewModel } from '@components/notification-list/notification-list-table/notification-list-table.component';
import { LogService } from '@services/api/log.service';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-base-layout',
  standalone: true,
  imports: [CommonModule, RouterModule, HeaderComponent, SidenavComponent],
  templateUrl: './base-layout.component.html',
  styleUrl: './base-layout.component.scss',
})
export class BaseLayoutComponent implements OnInit {
  /** 掲示板メッセージ一覧 */
  public notificationList: NotificationViewModel[] = [];

  /** サイドメニューの開閉切り替えの有効化 */
  public sidenavToggleEnable = false;

  /** サイドメニューの開閉状態 */
  public sidenavOpened = true;

  /** サイドメニューのモード */
  public sidenavMode: MatDrawerMode = 'side';

  /** サイドメニューのタイプ */
  public sidenavType: SidenavType = 'tenant';

  /** サイドメニュー用の権限タイプ */
  public sidenavRole: SidenavRoleType = 'tenant_viewer';

  /** 掲示板メッセージ最終表示ポップアップ日時*/
  public lastReadAt: string = '';

  constructor(
    breakpointObserver: BreakpointObserver,
    private authService: AuthService,
    private systemAdminService: SystemAdminService,
    private notificationService: NotificationService,
    private logService: LogService,
    route: ActivatedRoute,
  ) {
    breakpointObserver.observe(Breakpoints.WebLandscape).subscribe((result) => {
      // Webサイズ以上になった場合はサイドメニューを常時表示する
      // 以下の場合はサイドメニューをボタンで開けるようにする
      if (result.matches) {
        this.sidenavToggleEnable = false;
        this.sidenavOpened = true;
        this.sidenavMode = 'side';
      } else {
        this.sidenavToggleEnable = true;
        this.sidenavOpened = false;
        this.sidenavMode = 'over';
      }
    });

    // ログインユーザーの権限を取得する
    const tenant = route.snapshot.data['loggedInUser'].tenant;
    this.sidenavRole = tenant.roleId;
    const isSystemAdmin = this.sidenavRole === 'system_admin';
    const isSelectedAdminMenu =
      this.systemAdminService.isSelectedSystemAdminTenant;
    this.sidenavType =
      isSystemAdmin && isSelectedAdminMenu ? 'admin' : 'tenant';
  }

  public ngOnInit(): void {
    this.findNotificationList(new Date());
  }

  /** ログアウト */
  public async logout() {
    try {
      await firstValueFrom(this.logService.add('logout'));
    } finally {
      this.authService.logout();
    }
  }

  /**
   * 掲示板メッセージ一覧ポップアップ表示
   */
  public findNotificationList(historyTo: Date): void {
    this.notificationService.findListForUser(historyTo).subscribe({
      next: (notificationList) => {
        // ポップアップ最終表示日時の取得
        if (notificationList.lastReadAt) {
          this.lastReadAt = notificationList.lastReadAt;
        }

        // 取得したメッセージ一覧をリストへ追加
        this.notificationList = [
          ...this.notificationList,
          ...notificationList.messageList,
        ];
      },
      error: () => {
        console.warn('Failed to find notification list.');
      },
    });
  }

  /**
   * 掲示板メッセージ一覧ポップアップ表示のリフレッシュ
   */
  public refreshNotificationList(): void {
    this.notificationList = [];
    this.findNotificationList(new Date());
  }
}
